import { getConsentsBySRDId, getList } from "@/apis/register/get";
import type { Consent } from "@/apis/register/get/type";
import { postResetPassword, postVerifyAccount } from "@/apis/register/post";
import { LayoutEnum } from "@/configs/enum";
import { authenticated, isNominationCommittee } from "@/utils/auth";
import ApplicantInformationView from "@/views/ApplicantInformationV2View.vue";
import ForgotPasswordView from "@/views/ForgotPasswordView.vue";
import HomeView from "@/views/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import ProfileView from "@/views/ProfileView.vue";
import RegisterView from "@/views/RegisterView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import SubscriptionView from "@/views/SubscriptionView.vue";
import VerifyAccountView from "@/views/VerifyAccountView.vue";
import _ from "lodash";
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from "vue-router";
import { ROUTE_NAMES } from "./constants";

async function checkExistForm(to: RouteLocationNormalized) {
  const formType = (to.params?.formType as string).toLowerCase();
  const senateRecruitDetailId = to.params?.senateRecruitDetailId as string;

  if (!isValidFormType(formType) || !senateRecruitDetailId) {
    return getNotFoundRoute(to);
  }

  try {
    const itemIndex = await getItemIndex(formType, senateRecruitDetailId);
    if (itemIndex > -1) return;
  } catch (error) {
    console.error("Error fetching list:", error);
    return getNotFoundRoute(to);
  }
}

function isValidFormType(formType: string) {
  const validTypes = ["cct", "ec", "nacc", "nbtc", "nhrc", "om", "pmec", "sao"];
  return validTypes.includes(formType);
}

function getNotFoundRoute(to: RouteLocationNormalized) {
  return {
    name: ROUTE_NAMES.NOT_FOUND,
    params: { pathMatch: to.path.substring(1).split("/") },
    query: to.query,
    hash: to.hash,
  };
}

async function getItemIndex(formTypeParam: string, SRDId: string) {
  const { status, data } = await getList();
  if (status === 200) {
    return data.findIndex(
      (item) =>
        item.type.toLowerCase() === formTypeParam && item.id === Number(SRDId),
    );
  }
  return -1;
}

async function checkActiveConsent(to: RouteLocationNormalized) {
  const formType = (to.params?.formType as string).toLowerCase();
  const senateRecruitDetailId = to.params?.senateRecruitDetailId as string;

  try {
    const { status, data } = await getConsentsBySRDId(senateRecruitDetailId);
    if (status === 200 && data?.active_id.length) {
      return {
        name: ROUTE_NAMES.OLD_REGISTER,
        params: {
          formType,
          senateRecruitDetailId,
          candidateInfoId: _.min(data?.active_id),
        },
      };
    }
  } catch (error) {
    console.error("Failed to load active consent:", error);
    return { name: ROUTE_NAMES.HOME };
  }
}

async function checkConsents(to: RouteLocationNormalized) {
  const senateRecruitDetailId = to.params?.senateRecruitDetailId as string;
  const candidateInfoId = to.params?.candidateInfoId as string;

  try {
    const { status, data } = await getConsentsBySRDId(senateRecruitDetailId);

    if (status === 200) {
      const item = _.find(
        data?.consents || [],
        (consent: Consent) => consent.id === Number(candidateInfoId),
      );
      if (!item) return getNotFoundRoute(to);
    }
  } catch (error) {
    console.error("Failed to load consents:", error);
    return { name: ROUTE_NAMES.HOME };
  }
}

async function verifyAccount(to: RouteLocationNormalized) {
  const uuid = to.params?.uuid as string;
  try {
    const { status } = await postVerifyAccount({ uuid });
    if (status !== 200) {
      return { name: ROUTE_NAMES.HOME };
    }
  } catch (error) {
    console.error("Error verifying account:", error);
    return { name: ROUTE_NAMES.HOME };
  }
}

async function resetPassword(to: RouteLocationNormalized) {
  const uuid = to.params?.uuid as string;
  try {
    const { status } = await postResetPassword({ uuid });
    if (status !== 200) {
      return { name: ROUTE_NAMES.HOME };
    }
  } catch (error) {
    console.error("Error resetting password:", error);
    return { name: ROUTE_NAMES.HOME };
  }
}

const router = createRouter({
  // history: createWebHistory(import.meta.env.BASE_URL),
  history: createWebHistory(),
  routes: [
    {
      path: "",
      name: ROUTE_NAMES.HOME,
      meta: { layout: LayoutEnum.PRIMARY_LAYOUT },
      component: HomeView,
    },
    {
      path: "/applicant-information",
      name: ROUTE_NAMES.APPLICANT_INFORMATION,
      meta: {
        layout: LayoutEnum.PRIMARY_LAYOUT,
        requiresAuth: true,
      },
      component: ApplicantInformationView,
    },
    {
      path: "/login",
      name: ROUTE_NAMES.LOGIN,
      meta: {
        layout: LayoutEnum.BLANK_BG_CRIMSON_LAYOUT,
        requiresAuth: false,
      },
      component: LoginView,
    },
    {
      path: "/forgot-password",
      name: ROUTE_NAMES.FORGOT_PASSWORD,
      meta: {
        layout: LayoutEnum.BLANK_BG_CRIMSON_LAYOUT,
        requiresAuth: false,
      },
      component: ForgotPasswordView,
    },
    {
      path: "/reset-password/:uuid",
      name: ROUTE_NAMES.RESET_PASSWORD,
      meta: {
        layout: LayoutEnum.BLANK_BG_CRIMSON_LAYOUT,
        requiresAuth: false,
      },
      component: ResetPasswordView,
      beforeEnter: [resetPassword],
    },
    {
      path: "/subscription",
      name: ROUTE_NAMES.SUBSCRIPTION,
      meta: {
        layout: LayoutEnum.BLANK_BG_CRIMSON_LAYOUT,
        requiresAuth: false,
      },
      component: SubscriptionView,
    },
    {
      path: "/verify-account/:uuid",
      name: ROUTE_NAMES.VERIFY_ACCOUNT,
      meta: {
        layout: LayoutEnum.BLANK_BG_CRIMSON_LAYOUT,
        requiresAuth: false,
      },
      component: VerifyAccountView,
      beforeEnter: [verifyAccount],
    },
    {
      path: "/register/:formType/:senateRecruitDetailId",
      name: ROUTE_NAMES.NEW_REGISTER,
      meta: { layout: LayoutEnum.SECONDARY_LAYOUT, requiresAuth: true },
      component: RegisterView,
      beforeEnter: [checkExistForm, checkActiveConsent],
    },
    {
      path: "/register/:formType/:senateRecruitDetailId/:candidateInfoId",
      name: ROUTE_NAMES.OLD_REGISTER,
      meta: { layout: LayoutEnum.SECONDARY_LAYOUT, requiresAuth: true },
      component: RegisterView,
      beforeEnter: [checkExistForm, checkConsents],
    },
    {
      path: "/profile",
      name: ROUTE_NAMES.PROFILE,
      meta: {
        layout: LayoutEnum.PRIMARY_LAYOUT,
        requiresAuth: true,
      },
      component: ProfileView,
    },

    {
      path: "/:pathMatch(.*)*",
      name: ROUTE_NAMES.NOT_FOUND,
      meta: { layout: LayoutEnum.BLANK_LAYOUT },
      component: NotFoundView,
    },
  ],
});

router.beforeEach((to, from, next) => {
  const auth = authenticated();
  const isNC = isNominationCommittee();

  if (to.name === ROUTE_NAMES.APPLICANT_INFORMATION && !isNC) {
    return next({ name: ROUTE_NAMES.HOME });
  }

  if (to.name !== ROUTE_NAMES.APPLICANT_INFORMATION && isNC) {
    return next({ name: ROUTE_NAMES.APPLICANT_INFORMATION });
  }

  if (to.meta.requiresAuth && !auth) {
    return next({ name: ROUTE_NAMES.LOGIN });
  }

  if (!to.meta.requiresAuth && auth && to.name !== ROUTE_NAMES.HOME) {
    return next({ name: ROUTE_NAMES.HOME });
  }

  if (to.name === from.name && to.params === from.params) {
    return next(false);
  }

  next();
});

export default router;
